<template>
    <section class="kb-visibility-section">
        <p class="kb-visibility-section__intro">
            {{ $t('knowledgebase.visibilityIntro', {total: connections.length}) }}
        </p>
        <div class="kb-visibility-section__empty" v-if="connections.length === 0">
            {{ $t('knowledgebase.visibilityNoConnections') }}
        </div>

        <div
            v-for="conn in connections"
            :key="conn.id"
            class="kb-visibility-row"
            :class="{'kb-visibility-row--has-pending': pendingStateFor(conn.id) != null}"
        >
            <div class="kb-visibility-row__icon">
                {{ (conn.channel_name[0] || '?').toUpperCase() }}
            </div>
            <div class="kb-visibility-row__body">
                <div class="kb-visibility-row__name">
                    {{ conn.channel_name }}
                    <span class="kb-visibility-row__type">{{ typeLabel(conn) }}</span>
                </div>
                <div class="kb-visibility-row__status" :class="statusClass(conn.id)">
                    {{ statusText(conn.id) }}
                </div>
            </div>
            <div class="kb-visibility-row__toggle">
                <label class="kb-toggle">
                    <input
                        type="checkbox"
                        :checked="isVisible(conn.id)"
                        :disabled="disabled || saving === conn.id"
                        @change="onToggle(conn, $event.target.checked)"
                    />
                    <span class="kb-toggle__slider"></span>
                </label>
            </div>
        </div>
    </section>
</template>

<script>
import { Api } from '../../../api';

// Per-channel visibility toggles for one article. Read-only when the
// article is staged for deletion (toggling visibility on a doomed article
// has no meaning).
//
// The backend collapses the 4×2 state machine — this component is purely
// presentational: toggle = "make visible" or "hide", let the server figure
// out the resulting row state. We pass `connections` in from the parent
// (already loaded for the dashboard); refreshing the article on a successful
// toggle keeps the parent's view of channel_exclusions in sync.

export default {
    name: 'kb-visibility-section',

    props: {
        article: {type: Object, required: true},
        // Pass connections in so the parent decides loading lifecycle.
        // Per-article fetches would N+1 the dashboard; the editor already
        // has the article + can hit /connections once.
        connections: {type: Array, default: () => []},
        disabled: {type: Boolean, default: false},
    },

    data() {
        return {
            // Tracks the connection id currently being saved so we can lock
            // its toggle. Multiple toggles in flight at once would be a UX
            // mess, so we just lock per-row.
            saving: null,
        };
    },

    computed: {
        exclusions() {
            return this.article.channel_exclusions || [];
        },
        exclusionsByConnection() {
            const map = {};
            for(const e of this.exclusions) map[e.connection] = e;
            return map;
        },
    },

    methods: {
        rowFor(connectionId) {
            return this.exclusionsByConnection[connectionId] || null;
        },
        // "Visible" reflects what will be live after the next publish — that
        // matches what the user is staging. A row in state `staged_add` means
        // "currently visible, will be hidden at publish" → the toggle should
        // show OFF. Conversely `staged_remove` is "currently hidden, will
        // become visible" → toggle ON.
        isVisible(connectionId) {
            const row = this.rowFor(connectionId);
            if(row == null) return true; // no row = visible
            if(row.state === 'live' || row.state === 'staged_add') return false;
            // staged_remove
            return true;
        },
        pendingStateFor(connectionId) {
            const row = this.rowFor(connectionId);
            if(row == null) return null;
            if(row.state === 'staged_add' || row.state === 'staged_remove') return row.state;
            return null;
        },
        statusClass(connectionId) {
            const pending = this.pendingStateFor(connectionId);
            if(pending) return 'kb-visibility-row__status--pending';
            return this.isVisible(connectionId)
                ? 'kb-visibility-row__status--visible'
                : 'kb-visibility-row__status--hidden';
        },
        statusText(connectionId) {
            const pending = this.pendingStateFor(connectionId);
            if(pending === 'staged_add') return this.$t('knowledgebase.visibilityPendingHide');
            if(pending === 'staged_remove') return this.$t('knowledgebase.visibilityPendingShow');
            if(this.isVisible(connectionId)) return this.$t('knowledgebase.visibilityVisible');
            return this.$t('knowledgebase.visibilityHidden');
        },
        typeLabel(conn) {
            if(conn.integration_type === 'synthflow') return this.$t('knowledgebase.channelTypePush');
            return this.$t('knowledgebase.channelTypeFetch');
        },
        async onToggle(conn, visible) {
            this.saving = conn.id;
            try {
                const res = await Api.knowledgebase.setArticleVisibility(
                    this.article.id, conn.id, visible,
                );
                // The endpoint returns the updated article — let the parent
                // replace its copy so all downstream consumers (publish modal
                // count, has_changes badge) refresh together.
                this.$emit('updated', res.body);
            } finally {
                this.saving = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-visibility-section {
    margin-top: 8px;
}

.kb-visibility-section__intro {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 14px;
}

.kb-visibility-section__empty {
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 6px;
    padding: 18px 20px;
    text-align: center;
    color: #888;
    font-style: italic;
}

.kb-visibility-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 6px;

    &--has-pending {
        border-color: #f5a623;
        background: rgba(245, 166, 35, 0.05);
    }

    &__icon {
        width: 36px;
        height: 36px;
        border-radius: 4px;
        background: #eaf3f8;
        color: #357;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        flex-shrink: 0;
    }

    &__body {
        flex: 1;
        min-width: 0;
    }

    &__name {
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__type {
        font-size: 0.65rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background: #f3f3f3;
        color: #666;
        border-radius: 3px;
        padding: 2px 6px;
        font-weight: 700;
    }

    &__status {
        font-size: 0.85rem;
        margin-top: 2px;

        &--visible {
            color: #4a7;
        }

        &--hidden {
            color: #888;
        }

        &--pending {
            color: #a76;
            font-weight: 600;
        }
    }

    &__toggle {
        flex-shrink: 0;
    }
}

// Bulma ships a `.switch` class that styles checkboxes globally, so we
// namespace ours to avoid the double-circle render.
.kb-toggle {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;

    input {
        opacity: 0;
        width: 0;
        height: 0;

        &:checked + .kb-toggle__slider {
            background-color: #4a7;
        }

        &:checked + .kb-toggle__slider::before {
            transform: translateX(20px);
        }

        &:disabled + .kb-toggle__slider {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    &__slider {
        position: absolute;
        cursor: pointer;
        top: 0; left: 0; right: 0; bottom: 0;
        background-color: #ccc;
        border-radius: 24px;
        transition: background-color 0.15s;

        &::before {
            position: absolute;
            content: '';
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background: white;
            border-radius: 50%;
            transition: transform 0.15s;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-visibility-section.vue